import gamTimeouts from '../../consts/config/gamTimeouts';
import uamTimeouts from '../../consts/config/uamTimeouts';
import * as yapActionStatusTypes from '../../consts/yap/yapActionStatusTypes';
import * as yapRunCommandActionTypes from '../../consts/yap/yapRunCommandActionTypes';
import {
    setIsYapForceCheckRequest,
    setSyncLastRequestId,
    setYapLastRequestId,
    setYapSyncInProgress,
} from '../../state/ducks/yapEncrypted';
import {makeSelectIotDeviceData} from '../../state/selectors/iotDevice';
import {
    makeSelectAssetIdByType,
    makeSelectIsDeviceSyncInProgress,
    makeSelectIsYapAutoCleaningInProgress,
    makeSelectIsYapDeviceConnected,
    makeSelectIsYapFirmwareInProgress,
    makeSelectIsYapForceCheckRequest,
    makeSelectIsYapHolderConnected,
    makeSelectIsYapSynchronizeDataCollectionInProgress,
    makeSelectIsYapUpdateSettingsInProgress,
    makeSelectRequestActionTypeData,
    makeSelectSyncLastRequestId,
    makeSelectYapLastRequestId,
} from '../../state/selectors/yapEncrypted';
import {dispatch, getState} from '../../state/store';
import backendService from '../app/backendService';
import gamClientService from '../gam/gamClientService';
import gamWebSocketResponseService from '../gam/gamWebSocketResponseService';
import log from '../logger/log';
import {getErrorMapping} from '../mapping/yapMappings';
import uamClientService from '../uam/uamClientService';
import uamWebSocketResponseService from '../uam/uamWebSocketResponseService';
import yapWebSocketService from '../yap/yapWebSocketService';
import yapConnectionService from './yapConnectionService';
import yapFWUService from './yapFWUService';

let checkStatusScheduler;
let holderCheckStatusScheduler;

const setupCheckStatusScheduler = (isHolder) => {
    const isGamBackend = backendService.isGamBackend();
    const interval = isGamBackend ? gamTimeouts.GAM_CHECK_STATUS_INTERVAL : uamTimeouts.UAM_CHECK_STATUS_INTERVAL;
    const newTimeout = setTimeout(checkStatus, interval * 1000, isHolder);

    if (isHolder) {
        holderCheckStatusScheduler = newTimeout;
    } else {
        checkStatusScheduler = newTimeout;
    }
};

const createCheckStatusScheduler = (isHolder) => {
    if ((!isHolder && checkStatusScheduler) || (isHolder && holderCheckStatusScheduler)) return;

    log.info(`yapWebSocketService: check status scheduler is started isHolder: ${!!isHolder}`);
    setupCheckStatusScheduler(isHolder);
};

const stopCheckStatusScheduler = (isHolder) => {
    const msg = `yapWebSocketService: check status scheduler is stopped isHolder: ${!!isHolder}`;

    if (isHolder && holderCheckStatusScheduler) {
        clearTimeout(holderCheckStatusScheduler);
        holderCheckStatusScheduler = null;
        log.info(msg);
        return;
    }

    if (!isHolder && checkStatusScheduler) {
        clearTimeout(checkStatusScheduler);
        checkStatusScheduler = null;
        log.info(msg);
    }
};

const checkStatus = async (isHolder) => {
    const isWebSocketConnected = yapWebSocketService.isYapWebSocketConnected(isHolder);
    const isForceCheckRequest = makeSelectIsYapForceCheckRequest(isHolder)(getState());

    if (!isWebSocketConnected || isForceCheckRequest) {
        if (isForceCheckRequest) {
            dispatch(setIsYapForceCheckRequest(false, isHolder));
        }

        await checkRequestInternal(isHolder);
    }

    setupCheckStatusScheduler(isHolder);
};

const checkRequestInternal = async (isHolder) => {
    try {
        const state = getState();
        const isYapDeviceConnected = makeSelectIsYapDeviceConnected()(state);
        const isYapHolderConnected = makeSelectIsYapHolderConnected()(state);
        const {device} = makeSelectIotDeviceData()(state);
        const {isHolderConnected} = device || {};

        if ((!isYapDeviceConnected && !isHolder) || (isHolder && isHolderConnected && !isYapHolderConnected)) {
            await yapConnectionService.checkConnectivityStatus(isHolder);
        }

        await checkYapRequestStatus(isHolder);
    } catch (e) {
        log.info(`yapCheckStatusService: request check is ignored due error: ${e}`);
    }
};

const checkYapRequestStatus = async (isHolder, isTimeout = false) => {
    const state = getState();
    const assetId = makeSelectAssetIdByType(isHolder)(state);

    if (!assetId) return;

    const isGamBackend = backendService.isGamBackend();
    const syncRequestId = makeSelectSyncLastRequestId(isHolder)(state);
    const lastRequestId = makeSelectYapLastRequestId(isHolder)(state);
    const isSync = makeSelectIsDeviceSyncInProgress(isHolder)(state);
    const requestId = isSync ? syncRequestId : lastRequestId;

    if (!requestId) return true;

    const {status, error, endTime} = isGamBackend
        ? await gamClientService.checkGamRequestStatus(assetId, requestId, isSync)
        : await uamClientService.checkUamRequestStatus({assetId, requestId});
    const endTimeTS = endTime ? new Date(endTime) : new Date();

    if (status !== yapActionStatusTypes.PENDING) {
        log.info(
            `yapCheckStatusService: request is checked with status ${status} for requestId ${requestId} isHolder: ${isHolder}`
        );
        await updateYapInProgressStatus({status, error, endTimeTS, isSync, assetId, isHolder, isTimeout});

        if (isSync) {
            dispatch(setSyncLastRequestId(null, isHolder));
        } else {
            dispatch(setYapLastRequestId(null, isHolder));
        }
        return true;
    }

    return false;
};

const updateYapInProgressStatus = async ({status, error, endTimeTS, isSync, assetId, isHolder, isTimeout}) => {
    const statusCheckError = getErrorMapping(error);
    const state = getState();
    const isGamBackend = backendService.isGamBackend();
    const isSuccess = status === yapActionStatusTypes.SUCCESS;

    if (isSync) {
        const isDeviceSyncInProgress = makeSelectIsDeviceSyncInProgress()(state);
        const isHolderSyncInProgress = makeSelectIsDeviceSyncInProgress(true)(state);

        if ((isHolder && !isHolderSyncInProgress) || (!isHolder && !isDeviceSyncInProgress)) {
            return;
        }

        if (!isSuccess && isTimeout) {
            log.info('End synchronization process. Timeout.');
            dispatch(setYapSyncInProgress(false, isHolder));
            return;
        }

        await yapConnectionService.proceedRefreshInfo(status, assetId);
        return;
    }

    const isYapUpdateSettingsInProgress = makeSelectIsYapUpdateSettingsInProgress(isHolder)(state);

    if (isYapUpdateSettingsInProgress) {
        const {actionType} = makeSelectRequestActionTypeData()(state) || {};

        if (actionType === yapRunCommandActionTypes.FIND_DEVICE) {
            if (isGamBackend) return;

            await uamWebSocketResponseService.proceedFindDevice(status, endTimeTS);
            return;
        }

        if (actionType === yapRunCommandActionTypes.RESET_DEVICE) {
            if (isGamBackend) {
                gamWebSocketResponseService.proceedResetDevice(status, assetId);
            } else {
                uamWebSocketResponseService.proceedResetDevice(status, assetId);
            }
            return;
        }

        if (actionType === yapRunCommandActionTypes.CLEAN_DEVICE) {
            if (isGamBackend) return;

            await uamWebSocketResponseService.proceedCleanDevice(status, statusCheckError);
            return;
        }

        if (isGamBackend) {
            gamWebSocketResponseService.updateDeviceSettings(status, assetId);
        } else {
            uamWebSocketResponseService.updateDeviceSettings(status, assetId);
        }
        return;
    }

    const isYapAutoCleaningInProgress = makeSelectIsYapAutoCleaningInProgress()(state);

    if (isYapAutoCleaningInProgress) {
        if (isGamBackend) return;

        await uamWebSocketResponseService.proceedCleanDevice(status, statusCheckError);
        return;
    }

    const isYapSynchronizeDataCollectionInProgress = makeSelectIsYapSynchronizeDataCollectionInProgress()(state);
    const isDataCollectionInProgress = isYapSynchronizeDataCollectionInProgress;

    if (isDataCollectionInProgress && !isGamBackend) {
        await uamWebSocketResponseService.proceedSynchronizeDataCollection(status, assetId);
        return;
    }

    const isYapFirmwareInProgress = makeSelectIsYapFirmwareInProgress()(state);

    if (isYapFirmwareInProgress) {
        await yapFWUService.proceedFWU(status, assetId, statusCheckError);
    }
};

export default {
    checkYapRequestStatus,
    createCheckStatusScheduler,
    stopCheckStatusScheduler,
};
